import * as React from 'react'
import {Chip, TextField} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import {observer} from 'mobx-react-lite'

interface Props {
    label: string
    value: string[]
    keyPrefix: string
    onAdd(value: string): void
    onDelete(value: string, index: number): void
}

interface InputFieldProps {
    label: string
    onAdd(value: string): void
}

type ChangeEvent = React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>

function InputField({label, onAdd}: InputFieldProps) {
    const [value, setValue] = React.useState('')

    const onChange = React.useMemo(() => (e: ChangeEvent) => setValue(e.target.value), [setValue])
    const onKeyPress = React.useMemo(
        () => (e: React.KeyboardEvent) => {
            if (e.charCode !== 13) {
                return
            }

            onAdd(value)
            setValue('')
        },
        [onAdd, value, setValue]
    )

    return <TextField fullWidth value={value} label={label} onChange={onChange} onKeyPress={onKeyPress} />
}

function ChipInput({label, value, keyPrefix, onDelete, onAdd}: Props) {
    const addHandler = React.useCallback(
        function (next: string) {
            onAdd(next.trim())
        },
        [onAdd]
    )

    const deleteHandler = React.useCallback(
        (e: React.SyntheticEvent, index: number) => {
            let el = e.target as HTMLElement | null
            while (el && !el.classList.contains('ibt-chip-from-input')) {
                el = el.parentElement
            }

            if (!el) {
                return
            }

            const val = el.dataset.label
            if (typeof val === 'string') {
                onDelete(val, index)
            }
        },
        [onDelete]
    )

    return (
        <div>
            <div>
                {value.map((c, idx) => (
                    <Chip
                        key={`${keyPrefix}-chip-${idx}`}
                        label={c}
                        onDelete={e => deleteHandler(e, idx)}
                        deleteIcon={<CancelIcon className="ibt-chip-from-input" data-label={c} />}
                    />
                ))}
            </div>

            <InputField onAdd={addHandler} label={label} />
        </div>
    )
}

export default observer(ChipInput)
